import React, { JSX, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, LockReset as LockResetIcon } from '@mui/icons-material';
import { useAuth } from 'hooks/AuthContext';

export const ForgotPasswordForm = ({ onBack }: { onBack: () => void }): JSX.Element => {
  const { requestPasswordReset, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (await requestPasswordReset(email)) {
      setSubmitted(true);
    } else {
      setError('Something went wrong. Please try again.');
    }
  };

  if (submitted) {
    return (
      <Box textAlign="center" sx={{ mt: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Check your email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If an account with <strong>{email}</strong> exists, you will receive a password reset link
          shortly.
        </Typography>
        <Button variant="outlined" onClick={onBack} fullWidth>
          Back to Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <LockResetIcon color="primary" />
        <Typography variant="h6">Reset your password</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the email address associated with your account and we&apos;ll send you a reset link.
      </Typography>
      <TextField
        margin="normal"
        required
        fullWidth
        label="Email address"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        autoComplete="email"
        autoFocus
      />
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !email}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? <CircularProgress size={20} /> : 'Send reset link'}
      </Button>
      <Box textAlign="center">
        <Link component="button" variant="body2" onClick={onBack} underline="hover">
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );
};
